import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Alert, AlertDescription } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';
import { FieldError, FieldLegend, FieldSet } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';
// import { TextareaField } from '@nesso/ui/primitives/textarea-field';

import {
    TELEHEALTH_LINK_TYPES,
    type TelehealthSettingsValue,
    telehealthLinkSchema,
} from '@/entities/practice-member-settings';

// Every failure mode shares "Enter a valid link". `telehealthLinkSchema` doesn't cover the
// "linkType chosen, field untouched" case, so this local schema exists to add that message for it.
// `linkType` itself may be `null` on the stored value (not yet configured) — the enum here covers
// only what a submission may choose, which is why "Select a link type" still fires for it.
const telehealthLinkFormSchema = z
    .object({
        linkType: z.enum(TELEHEALTH_LINK_TYPES, { message: 'Select a link type' }),
        link: z.string().optional(),
        instructions: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.linkType !== 'SAME_FOR_ALL') return;

        if (!telehealthLinkSchema.safeParse(data.link).success) {
            ctx.addIssue({ code: 'custom', path: ['link'], message: 'Enter a valid link' });
        }
    });

type FormValues = z.input<typeof telehealthLinkFormSchema>;

type Props = {
    defaultSettings: TelehealthSettingsValue;
    isSubmitting: boolean;
    onSubmit: (value: TelehealthSettingsValue) => void;
};

export function TelehealthLinkForm({ defaultSettings, isSubmitting, onSubmit }: Props) {
    const form = useForm<FormValues>({
        resolver: zodResolver(telehealthLinkFormSchema),
        mode: 'onChange',
        defaultValues: {
            linkType: defaultSettings.linkType ?? undefined,
            link: defaultSettings.linkType === 'SAME_FOR_ALL' ? (defaultSettings.link ?? '') : '',
            instructions:
                (defaultSettings.linkType === 'SAME_FOR_ALL' && defaultSettings.instructions) || '',
        },
    });

    const linkType = form.watch('linkType');

    const handleSubmit = form.handleSubmit((values) => {
        onSubmit(
            values.linkType === 'SAME_FOR_ALL'
                ? {
                      linkType: 'SAME_FOR_ALL',
                      link: (values.link ?? '').trim(),
                      instructions:
                          values.instructions && values.instructions.trim() !== ''
                              ? values.instructions
                              : null,
                  }
                : { linkType: 'PER_APPOINTMENT', link: null, instructions: null },
        );
    });

    return (
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
            <Controller
                control={form.control}
                name="linkType"
                render={({ field, fieldState }) => {
                    // `fieldState.error` must be read unconditionally, not behind a short-circuit —
                    // react-hook-form tracks which proxy-backed properties a render actually reads
                    // to decide what to re-render on. Reading it only inside `showError && …`
                    // deregisters it while `showError` is false, and the error then never appears
                    // even once `showError` turns true, because that render never subscribed.
                    const error = fieldState.error;
                    const showError = fieldState.isTouched || form.formState.submitCount > 0;
                    return (
                        <FieldSet>
                            <FieldLegend>
                                How does your telehealth platform generate appointment links?
                            </FieldLegend>
                            <RadioGroup
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                            >
                                <RadioGroupItemField
                                    id="telehealth-link-type-same-for-all"
                                    value="SAME_FOR_ALL"
                                    label="Same URL for all appointments"
                                    error={showError && !!error}
                                />
                                <RadioGroupItemField
                                    id="telehealth-link-type-per-appointment"
                                    value="PER_APPOINTMENT"
                                    label="Different URL for each appointment"
                                    error={showError && !!error}
                                />
                            </RadioGroup>
                            {showError && <FieldError errors={[error]} />}
                        </FieldSet>
                    );
                }}
            />

            {linkType === 'SAME_FOR_ALL' && (
                <div className="flex w-full flex-col gap-6 rounded-sm bg-card p-4">
                    <Controller
                        control={form.control}
                        name="link"
                        render={({ field, fieldState }) => {
                            // Same rule as above — read unconditionally before gating on `showError`.
                            const errorMessage = fieldState.error?.message;
                            const showError =
                                fieldState.isTouched || form.formState.submitCount > 0;
                            return (
                                <InputField
                                    {...field}
                                    id="telehealth-link"
                                    label="Paste your telehealth link"
                                    disabled={isSubmitting}
                                    error={showError ? errorMessage : undefined}
                                />
                            );
                        }}
                    />
                    {/* Hidden until appointment reminders exist; the stored value is kept.
                    https://linear.app/nessoapp/issue/NSS-529/telehealth-settings-page#comment-74854117
                    <Controller
                        control={form.control}
                        name="instructions"
                        render={({ field }) => (
                            <TextareaField
                                {...field}
                                id="telehealth-instructions"
                                label="Instructions for your client (optional)"
                                description="These instructions will appear in appointment reminders"
                                disabled={isSubmitting}
                            />
                        )}
                    />
                    */}
                </div>
            )}

            {linkType === 'PER_APPOINTMENT' && (
                <Alert variant="cyan">
                    <Info className="size-4" />
                    <AlertDescription>
                        We don&apos;t have telehealth integrations just yet, so you&apos;ll add each
                        appointment link as you schedule
                    </AlertDescription>
                </Alert>
            )}

            <div>
                <Button type="submit" size="lg" disabled={isSubmitting}>
                    Save
                </Button>
            </div>
        </form>
    );
}
